import { useState, useMemo } from 'react'
import { Search, icons, type LucideIcon } from 'lucide-react'
import { useI18n } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ICON_CATEGORIES, resolveEntityTypeIcon } from './entity-type-icon'

export interface IconPickerProps {
  value: string | null | undefined
  onChange: (icon: string | null) => void
  disabled?: boolean
}

export function IconPicker({ value, onChange, disabled }: IconPickerProps) {
  const i18n = useI18n()
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setSearch('')
  }

  const searchResults = useMemo(() => {
    const q = search.toLowerCase()
    if (!q) return null
    return Object.keys(icons)
      .filter((name) => name.toLowerCase().includes(q))
      .slice(0, 100)
  }, [search])

  const SelectedIcon = value ? resolveEntityTypeIcon(value) : null

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start gap-2"
          disabled={disabled}
          data-testid="btn-icon-picker"
        >
          {SelectedIcon ? (
            <>
              <SelectedIcon className="h-4 w-4" />
              <span>{value}</span>
            </>
          ) : (
            <span className="text-muted-foreground">{i18n.types.settingsIconNone}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[520px] max-w-[90vw] p-3" align="start">
        <div className="flex items-center gap-2 px-1 pb-2 sticky top-0 bg-popover z-10">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={i18n.types.settingsIconSearch}
            className="h-7 border-0 p-0 focus-visible:ring-0 text-sm"
          />
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          <DropdownMenuItem
            onClick={() => { onChange(null); setOpen(false) }}
            className="gap-2 mb-1"
          >
            <span className="text-muted-foreground">{i18n.types.settingsIconNone}</span>
          </DropdownMenuItem>
          {searchResults !== null ? (
            searchResults.length === 0 ? (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                {i18n.types.settingsIconNoResults}
              </div>
            ) : (
              <div className="grid grid-cols-10 gap-0.5">
                {searchResults.map((name) => {
                  const IconComp = icons[name as keyof typeof icons] as LucideIcon
                  return (
                    <button
                      key={name}
                      type="button"
                      title={name}
                      data-testid={`icon-btn-${name}`}
                      onClick={() => { onChange(name); setOpen(false) }}
                      className="flex items-center justify-center h-8 w-8 rounded hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <IconComp className="h-4 w-4" />
                    </button>
                  )
                })}
              </div>
            )
          ) : (
            ICON_CATEGORIES.map(({ key, icons: catIcons }) => (
              <div key={key} className="mb-3">
                <div className="px-1 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide select-none">
                  {i18n.types.iconCategories[key as keyof typeof i18n.types.iconCategories]}
                </div>
                <div className="grid grid-cols-10 gap-0.5">
                  {catIcons.map(({ name, icon: Icon }) => (
                    <button
                      key={name}
                      type="button"
                      title={name}
                      data-testid={`icon-btn-${name}`}
                      onClick={() => { onChange(name); setOpen(false) }}
                      className="flex items-center justify-center h-8 w-8 rounded hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
